import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatIcon } from "@angular/material/icon";
import { catchError, forkJoin, of } from "rxjs";
import {
  DashboardGraphItem,
  DashboardKpi,
  GRAPH_COLORS,
  buildDonutStyle,
  normalizarGraficas,
  porcentaje,
} from "src/app/dashboard/shared/dashboard-graficas";
import { PestanaDashboard } from "src/app/dashboard/shared/pestana-dashboard";
import { Graficas } from "src/app/graficas/gestionGraficas/graficas.model";
import { IncidenciasService } from "src/app/incidencias/incidencias.service";

/**
 * Pestaña "Defectos" del Dashboard de Inicio: KPIs, dona por estatus,
 * proyectos con mas carga, actividad por mes y prioridades de atencion,
 * a partir de las graficas historicas de incidencias QA. Consulta al
 * montarse; el cascaron (dashini) la vuelve a consultar con actualizar().
 */
@Component({
  selector: "app-dashboard-defectos",
  standalone: true,
  templateUrl: "./dashboard-defectos.component.html",
  styleUrls: ["../../shared/dashboard-tarjetas.css"],
  imports: [CommonModule, MatIcon],
})
export class DashboardDefectosComponent implements OnInit, PestanaDashboard {
  incidenciaErrorCarga = "";
  incidenciaKpis: DashboardKpi[] = [];
  incidenciaEstatusItems: DashboardGraphItem[] = [];
  incidenciaProyectoItems: DashboardGraphItem[] = [];
  incidenciaMesItems: DashboardGraphItem[] = [];
  totalIncidencias = 0;
  incidenciaMesMax = 0;

  private cargandoDatos = false;
  /** La pestaña se destruye al cambiar de tab: corta la consulta en vuelo. */
  private readonly destroyRef = inject(DestroyRef);
  /** true desde que la pestaña se destruye; guarda el repintado (ver repintar()). */
  private destruido = false;

  private readonly incidentStatusColors: Record<string, string> = {
    ABIERTO: "#d32f2f",
    OBSERVACIONES: "#ef6c00",
    PROCESO: "#1565c0",
    REVISION: "#6a5acd",
    RESUELTO: "#00897b",
    VOBO: "#455a64",
    CANCELADO: "#8d8d8d",
  };

  constructor(
    private incidenciasService: IncidenciasService,
    // La vista NO se repinta sola cuando llega la respuesta HTTP: en este
    // arranque zone.js no queda parcheando XMLHttpRequest, asi que el
    // estado cambia y el DOM se queda en "Actualizando indicadores..."
    // hasta el siguiente clic. Se pide el repintado a mano, el mismo
    // patron que usa el resto de la casa (asistencia/mi-asistencia).
    // NO quitar por "innecesario".
    private cdr: ChangeDetectorRef,
  ) {
    // Se registra ANTES de cualquier suscripcion: los callbacks de
    // DestroyRef corren en orden de registro, asi que cuando
    // takeUntilDestroyed cierre el stream al destruir la pestaña (y con
    // ello dispare su complete) esto ya vale true.
    this.destroyRef.onDestroy(() => (this.destruido = true));
  }

  ngOnInit() {
    this.consultarDashboardIncidencias();
  }

  get cargando(): boolean {
    return this.cargandoDatos;
  }

  actualizar(): void {
    this.consultarDashboardIncidencias();
  }

  consultarDashboardIncidencias(): void {
    this.cargandoDatos = true;
    this.incidenciaErrorCarga = "";

    forkJoin({
      estatus: this.incidenciasService.consultaIncidenciaXs().pipe(catchError(() => of([] as Graficas[]))),
      proyectos: this.incidenciasService.consultaIncidenciaXp().pipe(catchError(() => of([] as Graficas[]))),
      meses: this.incidenciasService.consultaIncidenciaXd().pipe(catchError(() => of([] as Graficas[]))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.incidenciaEstatusItems = this.normalizarIncidenciaEstatus(res.estatus || []);
          this.incidenciaProyectoItems = normalizarGraficas(res.proyectos || []).slice(0, 8);
          this.incidenciaMesItems = normalizarGraficas(res.meses || [], false).slice(-8);
          this.incidenciaMesMax = Math.max(...this.incidenciaMesItems.map((item) => item.valor), 0);
          this.totalIncidencias = this.incidenciaEstatusItems.reduce((total, item) => total + item.valor, 0);
          this.incidenciaKpis = this.construirIncidenciaKpis();
          this.repintar();
        },
        error: (e) => {
          console.error("Error al cargar dashboard de incidencias:", e);
          // RxJS no llama a complete despues de un error: hay que apagar la
          // carga aqui o la tarjeta "Actualizando indicadores..." se queda.
          this.cargandoDatos = false;
          this.incidenciaErrorCarga = "No fue posible cargar la informacion de incidencias.";
          this.repintar();
        },
        complete: () => {
          this.cargandoDatos = false;
          this.repintar();
        },
      });
  }

  /**
   * Repinta la pestaña. detectChanges() sobre una vista ya destruida
   * truena, y el complete SI llega al destruir (takeUntilDestroyed cierra
   * el stream desde el propio onDestroy), de ahi el guardia.
   */
  private repintar(): void {
    if (this.destruido) {
      return;
    }
    // markForCheck ADEMAS de detectChanges: detectChanges refresca solo
    // ESTA vista, y el cascaron (dashini) lee el `cargando` de la pestaña
    // para habilitar su boton "Actualizar". Sin marcar el camino hacia la
    // raiz ese enlace se queda con el valor viejo: el boton se queda
    // deshabilitado para siempre y en desarrollo sale un NG0100.
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  getIncidentBarPercent(item: DashboardGraphItem, total = this.totalIncidencias): number {
    return porcentaje(item.valor, total);
  }

  getIncidentBarStyle(item: DashboardGraphItem, index: number, total = this.totalIncidencias): Record<string, string> {
    return {
      width: `${this.getIncidentBarPercent(item, total)}%`,
      background: item.color || GRAPH_COLORS[index % GRAPH_COLORS.length],
    };
  }

  getIncidentDonutStyle(): Record<string, string> {
    if (this.totalIncidencias <= 0 || this.incidenciaEstatusItems.length === 0) {
      return { background: "#eef1f5" };
    }

    return buildDonutStyle(this.incidenciaEstatusItems, this.totalIncidencias);
  }

  getIncidentStatusCount(nombre: string): number {
    return this.incidenciaEstatusItems
      .filter((item) => item.nombre === nombre)
      .reduce((total, item) => total + item.valor, 0);
  }

  private construirIncidenciaKpis(): DashboardKpi[] {
    const activos =
      this.getIncidentStatusCount("ABIERTO") +
      this.getIncidentStatusCount("OBSERVACIONES") +
      this.getIncidentStatusCount("PROCESO") +
      this.getIncidentStatusCount("REVISION");
    const cerrados = this.getIncidentStatusCount("RESUELTO") + this.getIncidentStatusCount("VOBO");
    const cancelados = this.getIncidentStatusCount("CANCELADO");
    const topProyecto = this.incidenciaProyectoItems[0];

    return [
      {
        label: "Defectos registrados",
        value: this.totalIncidencias,
        meta: "Historico de QA",
        icon: "bug_report",
        className: "ticket-kpi--total",
      },
      {
        label: "Activos",
        value: activos,
        meta: "Abierto, proceso y revision",
        icon: "running_with_errors",
        className: "ticket-kpi--active",
      },
      {
        label: "Cerrados",
        value: cerrados,
        meta: "Resuelto + VoBo",
        icon: "verified",
        className: "ticket-kpi--done",
      },
      {
        label: "Proyecto con mas carga",
        value: topProyecto?.valor || 0,
        meta: topProyecto?.nombre || "Sin proyecto",
        icon: "folder_special",
        className: "ticket-kpi--time",
      },
      {
        label: "Cancelados",
        value: cancelados,
        meta: "Fuera de flujo activo",
        icon: "block",
        className: "ticket-kpi--muted",
      },
    ];
  }

  private normalizarIncidenciaEstatus(items: Graficas[]): DashboardGraphItem[] {
    return normalizarGraficas(items).map((item) => ({
      ...item,
      color: this.incidentStatusColors[item.nombre] || item.color,
    }));
  }
}
